// ========================================
// GAMES PAGE - INSTRUCTIONS FOR BEGINNERS
// ========================================
//
// This file controls the Games page of the website.
//
// WHAT YOU CAN EDIT:
// 1. Game information (titles, descriptions, images, events)
// 2. Section headers (the category names)
// 3. Add new games or remove existing ones
//
// WHAT NOT TO EDIT:
// - The import statements at the top
// - The GameCard component (unless you know what you're doing)
// - The return section structure (just the text content)
//
// HOW TO ADD A NEW GAME:
// 1. Find the right category (battlefieldGames, otherWarGames, or otherGames)
// 2. Copy an existing game object (everything between { and })
// 3. Paste it in the category array
// 4. Change the id to a unique number
// 5. Update title, banner, tags, description, and events
//
// HOW TO CHANGE IMAGES:
// 1. Put your image files in the publicimages/ folder
// 2. Change the banner path to "images/your-image-name.jpg"
//
// HOW TO ADD TAGS:
// - Use "Main game" for important games
// - Use "High playerbase" for popular games
// - Use [] for no tags
//
// HOW TO ADD EVENTS:
// - Write the event text like "Game Nights, Custom Events"
// - Use "" (empty quotes) for no events

import React from "react";
import SectionHeader from "../../components/SectionHeader";
import "./index.css";

const Games = () => {
  // ========================================
  // GAME DATA - EDIT THIS SECTION TO UPDATE GAMES
  // ========================================
  //
  // TO ADD A NEW GAME:
  // 1. Copy an existing game object (the part inside { })
  // 2. Paste it in the appropriate category array below
  // 3. Change the id to a unique number (like 13, 14, etc.)
  // 4. Update the title, banner, tags, description, and events
  //
  // TO EDIT EXISTING GAMES:
  // - title: The game name that appears on the card
  // - banner: The image path (put images in publicimages/ folder)
  // - tags: Array of tag names (use "Main game" or "High playerbase")
  // - description: The text that describes the game
  // - events: The events text (leave empty string "" for no events)

  // TMain Games
  const mainGames = [
    {
      id: 7,
      title: "Squad",
         banner: "/images/squadgame2.jpeg",
      tags: ["Main Game", "Very High Playerbase"],
      description:
        "Advanced mil-sim warfare. Experience true combat in numerous different scenarios. Our most popular title by far.",
      events: "Game Nights, Clan Wars, Server Seeding, Clan Training, Foxtrot Friday, SuperFOB Saturday, Steamroll Sunday",
    },
    {
      id: 4, // UNIQUE ID
      title: "Enlisted", // GAME NAME
         banner: "/images/enlisted.jpg", // IMAGE PATH
      tags: ["Main Game", "Very High Playerbase"], // TAGS
      description:
        "World War II tactical shooter with authentic weapons and vehicles from the era. Our second most most popular game by far", // DESCRIPTION
      events: "Weekly Game Nights, Clan Wars, BCT", // EVENTS
    },
    {
      id: 8,
      title: "Helldivers II",
         banner: "/images/HD2W.jpg",
      tags: ["Main Game", "Very High Playerbase"],
      description:
        "Democratic, Space Warfare, Fighting for Super Earth, liberating and spreading democracy across the galaxy.",
      events: "Weekly Game Nights, Super Earth Cadet Academy",
    },
  ];

  // The Battlefield Series games
  const battlefieldGames = [
    {
      id: 1, // UNIQUE ID - change this if you add new games
      title: "Battlefield 6", // GAME NAME - change this to update the title
         banner: "/images/battlefield6.jpg", // IMAGE PATH - change this to use a different image
      tags: ["Main Game", "High Playerbase"], // TAGS - add/remove tags here (use "Main game" or "High playerbase")
      description:
        "An active and vibrant title, with a large and dedicated playerbase always ready for action. From casual matches to highly organized squads, there's never a shortage of teammates.", // DESCRIPTION - change this text
      events: "", // EVENTS - change this text, or use "" for no events
    },
    {
      id: 2, // UNIQUE ID
      title: "Battlefield 2042", // GAME NAME
         banner: "/images/battlefield-2042.jpg", // IMAGE PATH
      tags: [, "Medium Playerbase"], // TAGS - can be empty array [] for no tags
      description:
        "Modern warfare at its finest with cutting-edge technology and massive battles across diverse environments.", // DESCRIPTION
      events: "", // EVENTS
    },
    {
      id: 3, // UNIQUE ID
      title: "Legacy Battlefields", // GAME NAME
         banner: "/images/legacy_battlefields.jpg", // IMAGE PATH
      tags: [, "Medium Playerbase"], // TAGS - empty array means no tags will show
      description:
        "Classic Battlefield titles that have stood the test of time, perfect for veterans and newcomers alike.", // DESCRIPTION
      events: "", // EVENTS - empty string means no events will show
    },
  ];

  // Other War Games category
  const otherWarGames = [
    {
      id: 5,
      title: "Hell Let Loose",
         banner: "/images/hell_let_loose.jpg",
      tags: [, "High playerbase"],
      description:
        "Intense World War II experience with large-scale battles and realistic combat mechanics.",
      events: "",
    },
    {
      id: 6,
      title: "War Thunder",
         banner: "/images/war_thunder.jpg",
      tags: [, "High Playerbase"],
      description:
        "Comprehensive military vehicle combat with aircraft, tanks, and naval vessels.",
      events: "",
    },
    {
      id: 9,
      title: "Counter Strike 2",
         banner: "/images/cs2.png",
      tags: [, "High Playerbase"],
      description:
        "Terrorists vs Counter Terrorists in this classic tactical shooter",
      events: ""
    },
  ];

  // Other Games category
  const otherGames = [
    {
      id: 10, // UNIQUE ID
      title: "Roblox", // GAME NAME
         banner: "/images/roblox.jpg", // IMAGE PATH
      tags: [, "High Playerbase"], // TAGS
      description:
        "Endless possibilities in user-generated worlds and games, perfect for casual fun and creativity.", // DESCRIPTION
      events: "", // EVENTS - empty string means no events will show
    },
    {
      id: 11,
      title: "Hearts of Iron IV",
         banner: "/images/hearts_of_iron_iv.jpg",
      tags: [, " High Playerbase"],
      description:
        "Grand strategy game set during World War II with deep political and military mechanics.",
      events: "",
    },
    {
      id: 12, // UNIQUE ID
      title: "Minecraft", // GAME NAME
         banner: "/images/minecraft.jpg", // IMAGE PATH
      tags: [, "High Playerbase"], // TAGS - empty array means no tags will show
      description:
        "Unlimited creativity in a block-based world where you can build, explore, and survive.", // DESCRIPTION
      events: "", // EVENTS
    },
  ];

  // ========================================
  // GAME CARD COMPONENT - DON'T EDIT THIS SECTION
  // ========================================
  // This creates the visual card for each game
  // The card shows: image, title, tags, description, and events
  // You don't need to change anything here unless you want to add new features

  const GameCard = ({ game }) => (
    <div className="game-card">
      <div className="game-banner">
        <img src={game.banner} alt={game.title} />
      </div>
      <div className="game-content">
        <h3 className="game-title">{game.title}</h3>
        <div className="game-tags">
          {game.tags.map((tag, index) => (
            <span
              key={index}
              className={`game-tag tag-${
                index % 2 === 0 ? "primary" : "secondary"
              }`}
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="game-description">{game.description}</p>
        {game.events && (
          <div className="game-events">
            <strong>Events:</strong> {game.events}
          </div>
        )}
      </div>
    </div>
  );

  // ========================================
  // PAGE LAYOUT - EDIT SECTION HEADERS HERE
  // ========================================
  // This section controls how the page looks and what sections appear
  //
  // TO CHANGE SECTION NAMES:
  // - Change "The Battlefield Series" to whatever you want
  // - Change "Other War Games" to whatever you want
  // - Change "Other Games" to whatever you want
  //
  // TO ADD A NEW SECTION:
  // 1. Create a new array like battlefieldGames above
  // 2. Add a new SectionHeader and games-grid below
  // 3. Copy the pattern from the existing sections

  return (
    <div className="games-page">
      <div className="games-container">
        <h1 className="games-title">Games</h1>
        {/* SECTION 1: The Main Games */}
        <div className="games-section">
          <SectionHeader>Our Main Games</SectionHeader>{" "}
          {/* CHANGE THIS TEXT */}
          <div className="games-grid">
            {mainGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </div>
        {/* SECTION 2: Other War Games */}
        <div className="games-section">
          <SectionHeader>War/FPS Games</SectionHeader>{" "}
          {/* CHANGE THIS TEXT */}
          <div className="games-grid">
            {otherWarGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </div>
        {/* SECTION 3: The Battlefield Series */}
        <div className="games-section">
          <SectionHeader>The Battlefield Series</SectionHeader>{" "}
          {/* CHANGE THIS TEXT */}
          <div className="games-grid">
            {battlefieldGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </div>
        {/* SECTION 4: Other Games */}
        <div className="games-section">
          <SectionHeader>Other Games</SectionHeader> {/* CHANGE THIS TEXT */}
          <div className="games-grid">
            {otherGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Games;
